import { idToken, isSignedIn, signIn, clearSession } from "./auth";

let apiUrl = "";
export function setApiUrl(u: string) { apiUrl = u; }
export function getApiUrl(): string { return apiUrl; }

export interface Prototype {
  slug: string;
  sk: string;
  title: string;
  description?: string;
  owner: string;
  uploadedAt: string;
  currentVersion: number;
  versionCount: number;
  expiresAt?: number;
  kept?: boolean;
}

export interface CreateResponse {
  slug: string;
  uploadUrl: string;
  fields: Record<string, string>;
}

export interface VersionInfo {
  slug: string;
  sk: string;
  version: number;
  uploadedBy: string;
  uploadedAt: string;
  note?: string;
  status: "pending" | "ready" | "failed";
  error?: string;
  fileCount?: number;
  sizeBytes?: number;
  authMethod: "oidc" | "token";
  filesRetained: boolean;
}

export interface TokenResponse {
  token: string;
  expiresAt: number;
}

export interface CreateVersionResponse {
  version: number;
  uploadUrl: string;
  fields: Record<string, string>;
}

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  if (!isSignedIn()) {
    clearSession();
    signIn("none");
    throw new Error("Session expired — signing you back in…");
  }

  const res = await fetch(`${apiUrl}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${idToken()}`,
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401 || res.status === 403) {
    clearSession();
    signIn("none");
    throw new Error("Session expired — signing you back in…");
  }
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`);
  return data as T;
}

export const api = {
  list: () => req<{ prototypes: Prototype[] }>("GET", "/prototypes"),
  get: (slug: string) => req<Prototype>("GET", `/prototypes/${slug}`),
  create: (title: string, description?: string) =>
    req<CreateResponse>("POST", "/prototypes", { title, ...(description ? { description } : {}) }),
  del: (slug: string) => req<void>("DELETE", `/prototypes/${slug}`),
  keep: (slug: string) => req<{ kept: boolean }>("PUT", `/prototypes/${slug}/keep`),
  unkeep: (slug: string) => req<{ kept: boolean; expiresAt: number }>("PUT", `/prototypes/${slug}/unkeep`),
  listVersions: (slug: string) => req<{ versions: VersionInfo[] }>("GET", `/prototypes/${slug}/versions`),
  getVersion: (slug: string, version: number) => req<VersionInfo>("GET", `/prototypes/${slug}/versions/${version}`),
  generateToken: (slug: string) => req<TokenResponse>("POST", `/prototypes/${slug}/token`),
  createVersion: (slug: string, note?: string, uploadedBy?: string) =>
    req<CreateVersionResponse>("POST", `/prototypes/${slug}/versions`, {
      ...(note ? { note } : {}),
      ...(uploadedBy ? { uploadedBy } : {}),
    }),
};

export async function uploadZip(file: File, presigned: CreateResponse): Promise<void> {
  const form = new FormData();
  // presigned.fields already includes Content-Type and policy — append all fields first
  for (const [k, v] of Object.entries(presigned.fields)) form.append(k, v);
  // file MUST be last
  form.append("file", file);
  const res = await fetch(presigned.uploadUrl, { method: "POST", body: form });
  if (!res.ok) throw new Error(`S3 upload failed: ${res.status}`);
}
